import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    BatImpactChart,
    Button,
    Checkbox,
    HeadingChart,
    LoadingSpinner,
    number,
    pitchSubTypeColor,
    pitchTypeDisplayName,
    pitchTypeOrSubType,
} from 'yankees-react-utils';
import { HitterStrengths, useOktaQuery } from '../../../../data';
import { getBatterHand, matchesBool, useHandFilterTransition } from '../../batTrackingUtils';

const StyledWrapper = styled.div`
    margin-top: 12px;
    && > div {
        border: 1px solid rgb(188, 200, 209) !important;
        background-color: white;
    }
`;

const StyledCheckboxWrapper = styled.div`
    .nyy-checkbox-radio-container {
        display: flex;
    }
    & input[type="checkbox"]:checked + label::before {
        background-color: ${(props) => props.$color} !important;
        border-color: ${(props) => props.$color} !important;
    }
    & input[type="checkbox"] + label::before {
        border-radius: ${(props) => (props.$square ? '0' : '100%')};
    }
`;

const pitchOrders = [10, 9, 8, 33, 3, 34, 4, 7, 2];
const getPitchOrderValue = (pt) => {
    const index = pitchOrders.indexOf(pt);

    return index > -1 ? index : 100 + pt;
};
const hasValue = (value) => value !== null && value !== undefined;
const hasChartData = (pitch) => !!pitch.trackedswings;
const hasBatLocation = (pitch) => hasValue(pitch.hbat) && hasValue(pitch.vbat);
const isOverallCountBucket = (value) => Number(value ?? 0) === 0;

const convertFilterToPitchFX = (filter) => {
    let pitcherRole = null;
    switch (filter.pitcherRole) {
    case 1:
        pitcherRole = 1;
        break;
    case 2:
        pitcherRole = 0;
        break;
    default:
        pitcherRole = null;
    }
    let counts = null;
    const countArr = ['0', '10', '20', '30', '1', '11', '21', '31', '2', '12', '22', '32'];
    switch (filter.counts.join()) {
    case '-2':
        counts = '(0,10,20,30,1,11,21,31)';
        break;
    case '-3':
        counts = '(2,12,22,32)';
        break;
    case '-1':
        counts = null;
        break;
    default:
        counts = `(${filter.counts.map((x) => countArr[x]).join()})`;
    }
    return {
        baseState: filter.baseState === -1 ? null : filter.baseState,
        counts,
        result: filter.result.indexOf(-1) >= 0 ? null : filter.result,
        zoneLocation: filter.zoneLocation === -1 ? null : filter.zoneLocation,
        pitchLocation: filter.pitchLocation.length > 0 ? filter.pitchLocation.map((rec) => (10 * rec.locationx) + rec.locationy) : null,
        pitcherRole,
        levelIds: filter.levelIds,
        startDate: filter.startDate,
        endDate: filter.endDate,
    };
};

const buildPitchTypesSeries = (
    pitches,
    repertoire,
    islhb,
    islhp,
) => repertoire.map((r) => {
    const pitchType = pitchTypeDisplayName(r);
    return {
        id: `${r.fxtype}-${r.fxsubtype}`,
        name: pitchType,
        color: pitchSubTypeColor({
            type: r.fxtype,
            fxsubtype: r.fxsubtype,
        }),
        data: pitches.filter((p) => hasChartData(p) && p.fxtype === r.fxtype && p.fxsubtype === r.fxsubtype
            && matchesBool(p.islhb, islhb) && matchesBool(p.islhp, islhp))
            .map((p) => ({
                fxtype: p.fxtype,
                fxsubtype: p.fxsubtype,
                hbat: p.hbat,
                vbat: p.vbat,
                heading: p.heading,
                inz: p.inz,
                marker: {
                    symbol: p.inz ? 'square' : 'circle',
                    radius: 5,
                    lineWidth: 1,
                    lineColor: '#fff',
                    states: { hover: { radiusPlus: 10, lineWidth: 2, lineColor: '#fff' } },
                },
            })),
    };
});

export default function Charts({ playerId, filter, levels }) {
    const { t: translate } = useTranslation();
    const { chartFilter, isUpdating } = useHandFilterTransition(filter);
    const {
        baseState,
        counts,
        endDate,
        levelIds,
        pitchLocation,
        pitcherRole,
        result,
        startDate,
        zoneLocation,
    } = filter;
    const requestFilter = useMemo(() => convertFilterToPitchFX({
        baseState,
        counts,
        endDate,
        levelIds,
        pitchLocation,
        pitcherRole,
        result,
        startDate,
        zoneLocation,
    }), [
        baseState,
        counts,
        endDate,
        levelIds,
        pitchLocation,
        pitcherRole,
        result,
        startDate,
        zoneLocation,
    ]);
    const { data: batTracking, isLoading } = useOktaQuery(
        ['hitterStrengthsBatTracking', playerId, requestFilter],
        (t) => HitterStrengths.getBatTracking(t, playerId, requestFilter),
        { enabled: !!playerId && !!levels?.length, cacheTime: 0 },
    );

    const repertoire = useMemo(() => {
        const pitchTypes = batTracking?.repertoire?.filter((p) => p.fxtype > 1) ?? [];
        return [...new Map(pitchTypes.map((pt) => [`${pt.fxtype}-${pt.fxsubtype}`, pt])).values()];
    }, [batTracking]);

    const [selectedPitchTypes, setSelectedPitchTypes] = useState([]);
    const initializedPlayerId = useRef(null);

    useEffect(() => {
        if (repertoire.length && initializedPlayerId.current !== playerId) {
            initializedPlayerId.current = playerId;
            setSelectedPitchTypes(repertoire.map((r) => ({ fxtype: r.fxtype, fxsubtype: r.fxsubtype, inz: null })));
        }
    }, [playerId, repertoire]);

    const orderedPitchTypes = [...repertoire]
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredOrderedPitchTypes = [...repertoire]
        .filter((r) => selectedPitchTypes.some((spt) => spt.fxtype === r.fxtype && spt.fxsubtype === r.fxsubtype))
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredPitches = batTracking?.hitterbattracking
        ?.filter((p) => selectedPitchTypes.some((spt) => spt.fxtype === p.fxtype && spt.fxsubtype === p.fxsubtype && p.inz === spt.inz && isOverallCountBucket(p.countbucket))) ?? [];

    const headingTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />Heading: ${number(p.heading, 1)}`;
    const batImpactTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />HBat: ${number(p.hbat, 1)}<br />VBat: ${number(p.vbat, 1)}`;

    const isLhp = Number(chartFilter.pitcherHand) === 2;
    const allSeries = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes, null, isLhp);
    const lhbSeries = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes, true, isLhp);
    const rhbSeries = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes, false, isLhp);
    const headingSeries = allSeries.map((s) => ({
        ...s,
        data: s.data.filter((x) => x.heading !== null).map((d) => ({
            ...d,
            x: d.heading,
            y: filteredOrderedPitchTypes.findIndex(((r) => r.fxtype === d.fxtype && r.fxsubtype === d.fxsubtype)),
        })),
    }));
    const lhbBatImpactSeries = lhbSeries.map((s) => ({
        ...s,
        data: s.data.filter(hasBatLocation).map((d) => ({
            ...d,
            x: d.hbat,
            y: d.vbat,
        })),
    }));
    const rhbBatImpactSeries = rhbSeries.map((s) => ({
        ...s,
        data: s.data.filter(hasBatLocation).map((d) => ({
            ...d,
            x: d.hbat,
            y: d.vbat,
        })),
    }));

    const pitchTypeLeagueAverages = useMemo(() => batTracking?.hbatvbatellipses?.filter((e) => selectedPitchTypes.some((spt) => matchesBool(e.islhp, isLhp) && e.fxtype === spt.fxtype && e.fxsubtype === spt.fxsubtype)).map((e) => ({
        fxtype: e.fxtype,
        fxsubtype: e.fxsubtype,
        x: e.centerx,
        y: e.centerz,
        minorRadius: e.minorrad,
        majorRadius: e.majorrad,
        angle: e.angle,
        islhb: e.islhb,
    })) ?? [], [batTracking, isLhp, selectedPitchTypes]);

    const onPitchTypeClick = (pitchType) => {
        if (selectedPitchTypes.some((spt) => spt.fxtype === pitchType.fxtype && spt.fxsubtype === pitchType.fxsubtype && spt.inz === pitchType.inz)) {
            setSelectedPitchTypes(
                selectedPitchTypes.filter((spt) => spt.fxtype !== pitchType.fxtype || spt.fxsubtype !== pitchType.fxsubtype || spt.inz !== pitchType.inz),
            );
        } else {
            setSelectedPitchTypes([...selectedPitchTypes, pitchType]);
        }
    };

    const hasData = batTracking?.hitterbattracking?.some((p) => hasChartData(p) && matchesBool(p.islhp, isLhp));
    const isLhb = getBatterHand(chartFilter) === 2;
    const batImpactSeries = isLhb ? lhbBatImpactSeries : rhbBatImpactSeries;
    const batImpactLeagueAverages = pitchTypeLeagueAverages.filter((e) => matchesBool(e.islhb, isLhb));

    return (
        <StyledWrapper>{isLoading || isUpdating ? <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}><LoadingSpinner /></div>
            : (
                <div className="py-4 mt-3 rounded">
                    {hasData ? (
                        <div className="d-flex justify-content-around">
                            <div className="text-center">
                                <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-timing')}</div>
                                <HeadingChart
                                    seriesData={headingSeries}
                                    orderedPitchTypes={filteredOrderedPitchTypes.map((pt) => pt.fxsubtype || pt.fxtype)}
                                    tooltipFormatter={headingTooltipFormatter}
                                    showLeagueAverageEllipses
                                    disableJitter
                                    min={-20}
                                    max={50}
                                    sizeMultiplier={0.75}
                                />
                                <div className="d-flex justify-content-between ms-2 ps-5 pe-3">
                                    <div className="d-flex align-items-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="29px"
                                            viewBox="0 -960 960 960"
                                        >
                                            <path d="M360-200 80-480l280-280 56 56-183 184h647v80H233l184 184-57 56Z" />
                                        </svg>
                                        <div className="fs-4 fw-bold ms-1">{translate('pitcher-overview.sections.bat-tracking-late')}</div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="fs-4 fw-bold me-1">{translate('pitcher-overview.sections.bat-tracking-early')}</div>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="29px"
                                            viewBox="0 -960 960 960"
                                        >
                                            <path d="m600-200-57-56 184-184H80v-80h647L544-704l56-56 280 280-280 280Z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="text-center d-flex flex-column justify-content-between align-items-center">
                                <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-bat-locations')}</div>
                                <div className="d-flex">
                                    <div style={{ position: 'relative', top: isLhb ? 4 : 0, textAlign: 'center' }}>
                                        <BatImpactChart
                                            seriesData={batImpactSeries}
                                            isLhb={isLhb}
                                            pitcherView
                                            tooltipFormatter={batImpactTooltipFormatter}
                                            pitchTypeLeagueAverages={batImpactLeagueAverages}
                                            zoom
                                            sizeMultiplier={0.75}
                                        />
                                        <div className="fs-4 fw-bold mt-2">{translate(isLhb ? 'baseball.lhh' : 'baseball.rhh')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : <div className="py-5 text-center fs-6">{translate('pitcher-overview.no-data')}</div>}
                    <div className="d-flex justify-content-center align-items-center pb-2 mt-4 d-print-none">
                        {[...orderedPitchTypes].reverse().map((pt) => (
                            <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}`}>
                                <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)}>
                                    <Checkbox
                                        id={`hitter-${pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}`}
                                        checked={selectedPitchTypes
                                            .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype && spt.inz === null)}
                                        onClick={() => onPitchTypeClick({ ...pt, inz: null })}
                                    />
                                </StyledCheckboxWrapper>
                                <Button variant="link" onClick={() => setSelectedPitchTypes([{ ...pt, inz: null }])} className="ps-0">
                                    {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, true, null, translate)}{' '}
                                    ({batTracking?.hitterbattracking?.find((p) => (p.fxsubtype || p.fxtype) === (pt.fxsubtype || pt.fxtype) && p.inz === null && isOverallCountBucket(p.countbucket) && matchesBool(p.islhb, null) && matchesBool(p.islhp, isLhp))
                                        ?.trackedswings ?? 0})
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </StyledWrapper>
    );
}

Charts.propTypes = {
    playerId: PropTypes.number.isRequired,
    filter: PropTypes.shape({
        baseState: PropTypes.number,
        batterHand: PropTypes.number,
        counts: PropTypes.arrayOf(PropTypes.number),
        endDate: PropTypes.instanceOf(Date),
        levelIds: PropTypes.arrayOf(PropTypes.number),
        pitchLocation: PropTypes.arrayOf(PropTypes.shape({
            locationx: PropTypes.number,
            locationy: PropTypes.number,
        })),
        pitcherHand: PropTypes.number,
        pitcherRole: PropTypes.number,
        result: PropTypes.arrayOf(PropTypes.number),
        startDate: PropTypes.instanceOf(Date),
        zoneLocation: PropTypes.number,
    }).isRequired,
    levels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
